import React, { useState, useCallback, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Alert,
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Checkbox,
  Tooltip,
  TextField,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import SaveIcon from '@mui/icons-material/Save';
import { listCorals, createExport, modifyCoralAssignments, getCoralByRfid } from '../services/api';
import { CoralResponse } from '../types';
import MultiRfidSearchComponent from '../components/MultiRfidSearchComponent';
import { brand, accent } from '../theme';

// Helper function to format date for backend (removes milliseconds from ISO string)
const formatDateForBackend = (dateString: string): string => {
  return new Date(dateString).toISOString().replace(/\.\d{3}Z$/, 'Z');
};

interface ExportFormData {
  company: string;
  exportName: string;
  exportDate: string;
  cites: string;
}

const CreateExports: React.FC = () => {
  // Coral data state
  const [corals, setCorals] = useState<CoralResponse[]>([]);
  const [filteredCorals, setFilteredCorals] = useState<CoralResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);

  // Pagination state
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [cursorHistory, setCursorHistory] = useState<string[]>([]);

  // Selection state
  const [selectedCorals, setSelectedCorals] = useState<Set<string>>(new Set());

  // Export form state
  const [exportFormData, setExportFormData] = useState<ExportFormData>({
    company: '',
    exportName: '',
    exportDate: new Date().toISOString().split('T')[0], // Default to today
    cites: '',
  });

  // Search state
  const [searching, setSearching] = useState(false);
  const [searchMode, setSearchMode] = useState<'all' | 'rfid'>('all');

  // Create export dialog state
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [creating, setCreating] = useState(false);

  // Snackbar state
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');

  // Fetch coral data with pagination
  const fetchCorals = useCallback(async (cursor?: string, addToHistory: boolean = true) => {
    try {
      setLoading(true);
      const response = await listCorals(20, cursor);

      setCorals(response.items || []);
      setFilteredCorals(response.items || []);
      setNextCursor(response.nextCursor || null);

      // Add to history for pagination (except when refreshing)
      if (addToHistory && cursor) {
        setCursorHistory((prev) => {
          if (!prev.includes(cursor)) {
            return [...prev, cursor];
          }
          return prev;
        });
      }

      setError(null);
    } catch (err: any) {
      console.error('Error fetching corals:', err);
      setError(err.message || 'Failed to load coral data');
      setCorals([]);
      setFilteredCorals([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCorals();
  }, [fetchCorals]);

  const handleRefresh = async () => {
    try {
      setRefreshing(true);
      setError(null);
      setSearchMode('all');
      setCursorHistory([]);
      setNextCursor(null);
      setSelectedCorals(new Set());

      await fetchCorals(undefined, false);
    } catch (err: any) {
      console.error('Error refreshing corals:', err);
      setError(err.message || 'Failed to refresh coral data');
    } finally {
      setRefreshing(false);
    }
  };

  // Pagination handlers
  const handleNextPage = () => {
    if (nextCursor && searchMode === 'all') {
      fetchCorals(nextCursor);
    }
  };

  const handlePrevPage = () => {
    if (cursorHistory.length > 0) {
      const prevCursor = cursorHistory[cursorHistory.length - 2];
      setCursorHistory((prev) => prev.slice(0, -1));
      fetchCorals(prevCursor, false);
    } else {
      // Go to first page
      setCursorHistory([]);
      fetchCorals(undefined, false);
    }
  };

  // Selection handlers
  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const newSelected = new Set(displayedCorals.map((coral) => coral.coralId));
      setSelectedCorals(newSelected);
    } else {
      setSelectedCorals(new Set());
    }
  };

  const handleSelectCoral = (coralId: string) => {
    const newSelected = new Set(selectedCorals);
    if (newSelected.has(coralId)) {
      newSelected.delete(coralId);
    } else {
      newSelected.add(coralId);
    }
    setSelectedCorals(newSelected);
  };

  // Search handler
  const handleSearch = useCallback(
    async (rfids: string[]) => {
      if (!rfids || rfids.length === 0) {
        setFilteredCorals(corals);
        setSearchMode('all');
        return;
      }

      try {
        setSearching(true);
        setError(null);

        const results: CoralResponse[] = [];

        for (const rfid of rfids) {
          try {
            const result = await getCoralByRfid(rfid.trim());
            if (result) {
              results.push(result);
            }
          } catch (err: any) {
            console.warn(`RFID ${rfid} not found:`, err?.message || err);
          }
        }

        setFilteredCorals(results);
        setSearchMode('rfid');
      } catch (err: any) {
        console.error('Error searching by RFIDs:', err);
        let errorMessage = 'Search failed';
        if (err.response?.data?.errors) {
          errorMessage = err.response.data.errors;
        } else if (err.message) {
          errorMessage = err.message;
        }
        setError(errorMessage);
        setSearchMode('rfid');
      } finally {
        setSearching(false);
      }
    },
    [corals]
  );

  // Export form handlers
  const handleExportFormChange = (field: keyof ExportFormData, value: string) => {
    setExportFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleCreateExport = () => {
    // Validate form fields
    if (!exportFormData.company.trim()) {
      setError('Company name is required');
      return;
    }
    if (!exportFormData.exportDate) {
      setError('Export date is required');
      return;
    }

    // Clear any previous errors
    setError(null);
    setCreateDialogOpen(true);
  };

  const handleCreateDialogClose = () => {
    setCreateDialogOpen(false);
  };

  const handleCreateExportWithoutCorals = async () => {
    // Clear any previous errors
    setError(null);

    // Validate required fields
    if (!exportFormData.company.trim()) {
      setError('Company name is required');
      return;
    }
    if (!exportFormData.exportDate) {
      setError('Export date is required');
      return;
    }

    setCreating(true);
    try {
      const payload = {
        company: exportFormData.company.trim(),
        // Only include exportName if it has a value
        ...(exportFormData.exportName.trim() && { exportName: exportFormData.exportName.trim() }),
        exportDate: formatDateForBackend(exportFormData.exportDate),
        // Only include cites if it has a value
        ...(exportFormData.cites.trim() && { cites: exportFormData.cites.trim() }),
      };

      console.log('Creating export with payload:', payload);
      const result = await createExport(payload);
      console.log('Export creation result:', result);

      // Reset form after successful creation
      setExportFormData({
        company: '',
        exportName: '',
        exportDate: new Date().toISOString().split('T')[0],
        cites: '',
      });

      // Show success message
      const exportName = result?.items?.[0]?.exportName || 'Unnamed Export';
      setSnackbarMessage(`Export "${exportName}" created successfully!`);
      setSnackbarOpen(true);
    } catch (err: any) {
      console.error('Error creating export:', err);

      // Extract meaningful error message
      let errorMessage = 'Failed to create export';

      if (err.response?.data?.errors) {
        errorMessage = err.response.data.errors;
      } else if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.message) {
        errorMessage = err.message;
      }

      setError(`Export creation failed: ${errorMessage}`);
    } finally {
      setCreating(false);
    }
  };

  const handleConfirmCreate = async () => {
    // Clear any previous errors
    setError(null);

    if (!exportFormData.company.trim()) {
      setError('Company name is required');
      return;
    }

    setCreating(true);
    try {
      // Step 1: Create the export without coralIds
      const payload = {
        company: exportFormData.company.trim(),
        // Only include exportName if it has a value
        ...(exportFormData.exportName.trim() && { exportName: exportFormData.exportName.trim() }),
        exportDate: formatDateForBackend(exportFormData.exportDate),
        // Only include cites if it has a value
        ...(exportFormData.cites.trim() && { cites: exportFormData.cites.trim() }),
      };

      console.log('Creating export with payload:', payload);
      const exportResponse = await createExport(payload);
      const exportId = exportResponse.items[0]?.exportId;
      const exportName = exportResponse.items[0]?.exportName || 'Unnamed Export';
      console.log('Export created successfully with ID:', exportId);

      // Validate we got an exportId before proceeding
      if (!exportId) {
        throw new Error('Failed to retrieve export ID from response');
      }

      // Step 2: Assign corals if any are selected
      let assignmentMessage = `Export "${exportName}" created successfully!`;
      if (selectedCorals.size > 0) {
        try {
          const assignmentResponse = await modifyCoralAssignments(exportId, [
            {
              action: 'add',
              corals: Array.from(selectedCorals),
            },
          ]);
          console.log('Corals assigned successfully:', assignmentResponse);
          assignmentMessage = `Export "${exportName}" created with ${assignmentResponse.added.length} coral(s) assigned!`;
        } catch (assignmentErr: any) {
          // Export was created, but assignment failed
          console.error('Error assigning corals:', assignmentErr);

          // Extract meaningful error message for coral assignment
          let assignmentErrorMsg = assignmentErr.message || 'Unknown error';
          if (assignmentErr.response?.data?.errors) {
            assignmentErrorMsg = assignmentErr.response.data.errors;
          }

          setError(
            `Export "${exportName}" was created, but coral assignment failed: ${assignmentErrorMsg}`
          );
          assignmentMessage = `Export "${exportName}" created, but coral assignment failed.`;
        }
      }

      // Reset form and selection after successful creation
      setSelectedCorals(new Set());
      setExportFormData({
        company: '',
        exportName: '',
        exportDate: new Date().toISOString().split('T')[0],
        cites: '',
      });
      setCreateDialogOpen(false);

      // Show success message
      setSnackbarMessage(assignmentMessage);
      setSnackbarOpen(true);

      // Refresh coral fragments
      await handleRefresh();
    } catch (err: any) {
      console.error('Error creating export:', err);

      // Extract meaningful error message
      let errorMessage = 'Failed to create export';

      if (err.response?.data?.errors) {
        errorMessage = err.response.data.errors;
      } else if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.message) {
        errorMessage = err.message;
      }

      setError(`Export creation failed: ${errorMessage}`);

      // Refresh coral fragments even on error
      await handleRefresh();
    } finally {
      setCreating(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'ALIVE':
        return 'success';
      case 'DECEASED':
        return 'error';
      case 'EXPORTED':
        return 'info';
      default:
        return 'default';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const displayedCorals = searchMode === 'rfid' ? filteredCorals : corals;
  const isAllSelected =
    displayedCorals.length > 0 &&
    displayedCorals.every((coral) => selectedCorals.has(coral.coralId));
  const isSomeSelected = displayedCorals.some((coral) => selectedCorals.has(coral.coralId));

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      {/* Header */}
      <Typography variant="h4" gutterBottom>
        Create Export
      </Typography>
      <Typography variant="body1" color="text.secondary" paragraph>
        Enter export details below, then select coral fragments to include in the shipment.
      </Typography>

      {/* Export Details Form */}
      <Paper variant="outlined" sx={{ p: 3, mb: 2, boxShadow: 1, ...accent.coral }}>
        <Typography variant="h6" gutterBottom>
          Export Details
        </Typography>
        <Stack spacing={3}>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              label="Company"
              value={exportFormData.company}
              onChange={(e) => handleExportFormChange('company', e.target.value)}
              fullWidth
              variant="outlined"
              required
              helperText="Company or organization receiving the export"
            />
            <TextField
              label="Export Name (Optional)"
              value={exportFormData.exportName}
              onChange={(e) => handleExportFormChange('exportName', e.target.value)}
              fullWidth
              variant="outlined"
              placeholder="Enter a descriptive name for this export (optional)"
              helperText="Optional: A name to help identify this export later"
            />
          </Box>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              label="Export Date"
              value={exportFormData.exportDate}
              onChange={(e) => handleExportFormChange('exportDate', e.target.value)}
              variant="outlined"
              type="date"
              InputLabelProps={{ shrink: true }}
              sx={{ minWidth: 200 }}
              required
              helperText="Date of export shipment"
            />
            <TextField
              label="CITES (Optional)"
              value={exportFormData.cites}
              onChange={(e) => handleExportFormChange('cites', e.target.value)}
              fullWidth
              variant="outlined"
              multiline
              rows={1}
              placeholder="Enter CITES permit details if required"
              helperText="Optional: CITES permit information for regulated coral exports"
            />
          </Box>
        </Stack>
      </Paper>

      {/* Selection Summary */}
      <Paper
        variant="outlined"
        sx={{ p: 2, mb: 2, bgcolor: selectedCorals.size > 0 ? brand.navyLight : 'grey.50' }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography variant="h6">
            {selectedCorals.size > 0
              ? `${selectedCorals.size} coral fragments selected`
              : 'No coral fragments selected'}
          </Typography>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={selectedCorals.size > 0 ? handleCreateExport : handleCreateExportWithoutCorals}
            size="large"
            disabled={creating || !exportFormData.company.trim() || !exportFormData.exportDate}
          >
            {creating ? 'Creating...' : 'Create Export'}
          </Button>
        </Box>
      </Paper>

      {/* Search Section */}
      <Paper variant="outlined" sx={{ p: 2, mb: 2, boxShadow: 1, ...accent.coral }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 2,
          }}
        >
          <Typography variant="h6" sx={{ textAlign: 'center', flex: 1 }}>
            Search Coral Fragments
          </Typography>
          {searchMode === 'rfid' && (
            <Button
              variant="outlined"
              size="small"
              onClick={() => handleSearch([])}
              disabled={searching}
            >
              Show All
            </Button>
          )}
        </Box>
        <MultiRfidSearchComponent onSearch={handleSearch} isSearching={searching} />
        {searching && (
          <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Searching...
            </Typography>
          </Box>
        )}
      </Paper>

      {/* Results Section */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Paper variant="outlined" sx={{ p: 2, boxShadow: 1, ...accent.navy }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 2,
          }}
        >
          <Typography variant="h6">
            {searchMode === 'rfid'
              ? `Search Results (${displayedCorals.length})`
              : `Coral Fragments (${displayedCorals.length})`}
          </Typography>
          <Tooltip title="Refresh data">
            <Button
              startIcon={<RefreshIcon />}
              onClick={handleRefresh}
              disabled={loading || refreshing}
              size="small"
            >
              Refresh
            </Button>
          </Tooltip>
        </Box>

        <TableContainer sx={{ maxHeight: 600 }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    indeterminate={isSomeSelected && !isAllSelected}
                    checked={isAllSelected}
                    onChange={handleSelectAll}
                    disabled={loading || displayedCorals.length === 0}
                  />
                </TableCell>
                <TableCell>RFID</TableCell>
                <TableCell>Species</TableCell>
                <TableCell>Cut Date</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Created</TableCell>
                <TableCell>Last Updated</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    Loading coral fragments...
                  </TableCell>
                </TableRow>
              ) : displayedCorals.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    {searchMode === 'rfid'
                      ? 'No coral fragments match your search.'
                      : 'No coral fragments found.'}
                  </TableCell>
                </TableRow>
              ) : (
                displayedCorals.map((coral) => (
                  <TableRow
                    key={coral.coralId}
                    hover
                    selected={selectedCorals.has(coral.coralId)}
                    onClick={() => handleSelectCoral(coral.coralId)}
                    sx={{ cursor: 'pointer' }}
                  >
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selectedCorals.has(coral.coralId)}
                        onChange={() => handleSelectCoral(coral.coralId)}
                      />
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'medium' }}>{coral.rfid}</TableCell>
                    <TableCell>{coral.speciesCode}</TableCell>
                    <TableCell>{coral.cutDate || '—'}</TableCell>
                    <TableCell>
                      <Chip
                        label={coral.status}
                        size="small"
                        color={getStatusColor(coral.status) as any}
                      />
                    </TableCell>
                    <TableCell>{formatDate(coral.createdAt)}</TableCell>
                    <TableCell>{formatDate(coral.updatedAt)}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination Controls */}
        {searchMode === 'all' && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mt: 4,
            }}
          >
            <Button
              variant="outlined"
              onClick={handlePrevPage}
              disabled={loading || cursorHistory.length === 0}
              size="small"
            >
              Previous Page
            </Button>
            <Typography variant="body2" color="text.secondary">
              {(() => {
                const currentPage = cursorHistory.length + 1;
                const startRecord = (currentPage - 1) * 20 + 1;
                const endRecord = startRecord + displayedCorals.length - 1;
                return `Showing ${startRecord}-${endRecord} (Page ${currentPage})`;
              })()}
            </Typography>
            <Button
              variant="outlined"
              onClick={handleNextPage}
              disabled={loading || !nextCursor}
              size="small"
            >
              Next Page
            </Button>
          </Box>
        )}
      </Paper>

      {/* Create Export Dialog */}
      {/* Create Export Confirmation Dialog */}
      <Dialog open={createDialogOpen} onClose={handleCreateDialogClose} maxWidth="sm" fullWidth>
        <DialogTitle>Confirm Export Creation</DialogTitle>
        <DialogContent>
          <Typography variant="body1" paragraph>
            Are you sure you want to create this export shipment?
          </Typography>
          <Box sx={{ pl: 2 }}>
            <Typography variant="body2" color="text.secondary">
              <strong>Company:</strong> {exportFormData.company}
            </Typography>
            {exportFormData.exportName && (
              <Typography variant="body2" color="text.secondary">
                <strong>Export Name:</strong> {exportFormData.exportName}
              </Typography>
            )}
            <Typography variant="body2" color="text.secondary">
              <strong>Export Date:</strong> {exportFormData.exportDate || 'Not specified'}
            </Typography>
            {exportFormData.cites && (
              <Typography variant="body2" color="text.secondary">
                <strong>CITES:</strong> {exportFormData.cites}
              </Typography>
            )}
            <Typography variant="body2" color="text.secondary">
              <strong>Selected Corals:</strong> {selectedCorals.size} coral fragments
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCreateDialogClose} disabled={creating}>
            Cancel
          </Button>
          <Button onClick={handleConfirmCreate} variant="contained" disabled={creating}>
            {creating ? 'Creating...' : 'Create Export'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert onClose={() => setSnackbarOpen(false)} severity="success" sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default CreateExports;
